import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, AppBar, Toolbar, Typography, Avatar, IconButton, Menu, MenuItem, Divider } from '@mui/material';
import { Home, Search, Description, Folder, Schedule, People, Settings, Logout, Menu as MenuIcon, AccountCircle, DarkMode, LightMode, VpnKey, ShoppingCart, Gavel, Receipt, Storefront, LocalOffer } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useThemeContext } from '../../context/ThemeContext';

const SIDEBAR_WIDTH = 260;

const menuItems = [
  { text: 'Dashboard', icon: <Home />, path: '/dashboard' },
  { text: 'Búsqueda Licitaciones', icon: <Search />, path: '/licitaciones' },
  { text: 'MP Portal (Sesión)', icon: <VpnKey />, path: '/mp-licitaciones' },
  { text: 'MP Compra Ágil', icon: <ShoppingCart />, path: '/mp-licitaciones?tab=1' },
  { text: 'MP Órdenes de Compra', icon: <Receipt />, path: '/mp-licitaciones?tab=2' },
  { text: 'MP Trato Directo / Beta', icon: <Storefront />, path: '/mp-licitaciones?tab=3' },
  { text: 'Detalle Licitaciones', icon: <Description />, path: '/licitaciones/top' },
  { text: 'Documentos', icon: <Folder />, path: '/documentos' },
  { text: 'Tareas Programadas', icon: <Schedule />, path: '/tareas-programadas' },
  { text: 'Usuarios', icon: <People />, path: '/usuarios', adminOnly: true },
  { text: 'Configuración', icon: <Settings />, path: '/configuracion' },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Drawer variant="permanent" sx={{
        width: SIDEBAR_WIDTH, flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH, boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #003087 0%, #001a4d 100%)',
          color: 'white', border: 'none'
        }
      }}>
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <Typography variant="h4" sx={{
            fontWeight: 800, letterSpacing: 4,
            background: 'linear-gradient(135deg, #ffffff 0%, #00A3FF 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
          }}>UNIFIX</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Panel de Control</Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <List sx={{ px: 1, py: 1 }}>
          {menuItems.filter(item => !item.adminOnly || user?.role === 'admin').map((item) => (
            <ListItem key={item.text} onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 2, mb: 0.5, cursor: 'pointer',
                backgroundColor: isActive(item.path) ? 'rgba(0,163,255,0.2)' : 'transparent',
                borderLeft: isActive(item.path) ? '3px solid #00A3FF' : '3px solid transparent',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
              }}>
              <ListItemIcon sx={{ color: isActive(item.path) ? '#00A3FF' : 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} primaryTypographyProps={{
                fontSize: 14, fontWeight: isActive(item.path) ? 600 : 400,
                color: isActive(item.path) ? '#ffffff' : 'rgba(255,255,255,0.8)'
              }} />
            </ListItem>
          ))}
        </List>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: '#00A3FF', width: 36, height: 36 }}>
              {user?.full_name?.charAt(0) || user?.username?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }} noWrap>
                {user?.full_name || user?.username}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'capitalize' }}>
                {user?.role}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static" elevation={0} sx={{
          bgcolor: 'background.paper', color: 'text.primary',
          borderBottom: '1px solid', borderColor: 'divider'
        }}>
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {menuItems.find(m => isActive(m.path))?.text || 'Panel'}
            </Typography>
            <IconButton onClick={toggleTheme} sx={{ mr: 1 }}>
              {mode === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <AccountCircle />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { navigate('/configuracion'); setAnchorEl(null); }}>Mi Perfil</MenuItem>
              <MenuItem onClick={() => { logout(); navigate('/login'); }}>
                <Logout sx={{ mr: 1 }} /> Cerrar Sesión
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          <Outlet />
        </Box>

        {/* Footer */}
        <Box sx={{
          py: 2, px: 3, textAlign: 'center',
          borderTop: '1px solid', borderColor: 'divider',
          background: 'linear-gradient(135deg, #0047AB 0%, #00A3FF 100%)',
          color: 'white'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 2 }}>UNIFIX</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            &copy; {new Date().getFullYear()} Unifix SPA - Todos los derechos reservados
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
